import {useHalState} from '@synako/haligator';
import {Client} from '@synako/halitrek';
import {RootDesc as FFConnectRoot} from 'api-types/ffconnect-hal';
import {
  WebSocketClientMessage,
  WebSocketServerMessage,
} from 'api-types/ffconnect';
import {useCallback, useEffect, useRef} from 'react';

// Configuration de la reconnexion.
// Si la connexion WebSocket est coupée (réseau instable, serveur restart...),
// on ne retente pas immédiatement pour éviter de surcharger le serveur.
// Dès qu'une connexion réussit, le délai est réinitialisé à 1s.
const RECONNECT_CONFIG = {
  initialDelay: 1000, // Première tentative après 1 seconde
  maxDelay: 30_000, // Plafond : on ne dépasse jamais 30 secondes d'attente
  multiplier: 2, // Chaque échec double le délai (1s → 2s → 4s → 8s...)
};

// Délai au-delà duquel on considère la connexion comme morte si aucun message reçu.
// Le serveur envoie un heartbeat applicatif toutes les 30s.
// 75s = 2.5 cycles de heartbeat : laisse une marge pour les latences réseau.
const DELAI_CONNEXION_MORTE = 75_000;

// Parse un MessageEvent WebSocket en type union discriminé (le client reçoit des messages serveur)
function parseServerMessage(event: MessageEvent): WebSocketServerMessage {
  return JSON.parse(event.data as string) as WebSocketServerMessage;
}

// Sérialise un message client WebSocket avec typage (le client envoie des messages client)
function genClientMessage(message: WebSocketClientMessage): string {
  return JSON.stringify(message);
}

interface UseWebSocketOpts {
  /** Token Bearer optionnel. Si fourni (React Native), le hook répond
   *  à l'authentification du serveur. Si absent (web), l'auth se fait par cookie. */
  sessionToken?: string;
  /** Callback déclenché à chaque reconnexion (pas la première connexion).
   *  Permet d'invalider les caches pour rattraper les notifications manquées. */
  onReconnect?: () => void;
}

/**
 * Hook qui gère la connexion WebSocket globale pour les notifications temps réel.
 *
 * Fonctionnalités :
 * - Récupère l'URL WebSocket depuis `get-websocket`
 * - Se connecte automatiquement quand l'URL est disponible
 * - Gère l'authentification server-initiated (protocole authentication-required/token/success/error)
 * - Invalide le cache halitrek quand le serveur envoie un message `cache-invalidation`
 * - Reconnexion automatique en cas de déconnexion
 */
export function useWebSocket(client: Client, opts?: UseWebSocketOpts) {
  const {sessionToken, onReconnect} = opts ?? {};
  const api = client.go<FFConnectRoot>();

  // Suivre le lien get-websocket pour obtenir l'URL
  const websocketNav = api.follow('get-websocket');
  const {data: websocketState} = useHalState(websocketNav);

  // Refs pour persister les valeurs entre les re-renders
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectDelayRef = useRef(RECONNECT_CONFIG.initialDelay);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  // Intervalle de vérification périodique de la fraîcheur de la connexion
  const verificationFraicheurIntervalRef = useRef<ReturnType<
    typeof setInterval
  > | null>(null);

  // Ref pour stocker le token (permet de l'utiliser dans handleMessage sans recréer le callback)
  const tokenRef = useRef(sessionToken);
  tokenRef.current = sessionToken;

  // Ref pour le callback de reconnexion (évite de recréer connect à chaque changement)
  const onReconnectRef = useRef(onReconnect);
  onReconnectRef.current = onReconnect;

  // Distingue la première connexion des reconnexions
  const hasConnectedOnceRef = useRef(false);

  // Timestamp du dernier message reçu du serveur (heartbeat, auth-success, cache-invalidation...).
  // Permet de détecter les connexions mortes (ex: Cloud Run tue l'instance sans close frame).
  const dernierMessageRecuRef = useRef<number>(Date.now());

  // Handler des messages WebSocket
  // Note : les pings keep-alive sont des frames WebSocket natifs, invisibles ici.
  // Le heartbeat applicatif (envoyé par le serveur toutes les 30s) est visible ici.
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // Tout message reçu (y compris heartbeat) prouve que la connexion est vivante
      dernierMessageRecuRef.current = Date.now();

      try {
        const message = parseServerMessage(event);

        switch (message.type) {
          // Le serveur demande au client de s'authentifier (React Native uniquement)
          case 'authentication-required': {
            if (tokenRef.current) {
              wsRef.current?.send(
                genClientMessage({
                  type: 'authentication-token',
                  token: tokenRef.current,
                }),
              );
              client.logger.info(
                'WS: Authentication required, sending token...',
              );
            }
            break;
          }

          // Le serveur confirme que l'authentification a réussi
          case 'authentication-success':
            client.logger.info('WS: Authenticated successfully');
            // Sur reconnexion (pas la première connexion), invalider le cache
            // pour rattraper les notifications manquées pendant la coupure
            if (hasConnectedOnceRef.current) {
              onReconnectRef.current?.();
            }
            hasConnectedOnceRef.current = true;
            break;

          // Le serveur signale une erreur d'authentification (la connexion sera fermée par le serveur)
          case 'authentication-error':
            client.logger.warn('WS: Authentication error', message);
            break;

          // Heartbeat : rien à faire, la mise à jour de dernierMessageRecuRef est déjà faite plus haut
          case 'heartbeat':
            break;

          // Notification d'invalidation de cache
          case 'cache-invalidation': {
            // Si on est à l'origine de cette notification, on ignore car notre cache
            // est déjà à jour via la réponse du POST qui a déclenché l'event.
            // On vérifie simplement si l'event est déjà dans notre cache halitrek.
            // Note: client.resolve() convertit le path relatif en URL absolue pour matcher le cache.
            const resolvedUri = message.emitted_by
              ? client.resolve(message.emitted_by)
              : undefined;
            const isInCache = resolvedUri && client.resources.has(resolvedUri);

            if (!isInCache) {
              // Utilise halitrek pour invalider le cache
              client.handleResourceValidity(
                message.invalidates ?? [],
                message.deletes ?? [],
              );
            }
            break;
          }
        }
      } catch (error) {
        client.logger.warn('WS: Failed to parse message', error);
      }
    },
    [client],
  );

  /**
   * Vérifie si la connexion est morte (aucun message reçu depuis trop longtemps).
   * Si oui, force la fermeture pour déclencher la reconnexion automatique.
   * Code 4000 = code applicatif (range 4000-4999), déclenche le onclose → reconnexion.
   */
  const verifierConnexionMorte = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const elapsed = Date.now() - dernierMessageRecuRef.current;
      if (elapsed > DELAI_CONNEXION_MORTE) {
        client.logger.info(
          'WS: Stale connection detected (%dms without message), reconnecting...',
          elapsed,
        );
        wsRef.current.close(4000, 'Stale connection');
      }
    }
  }, [client]);

  // Fonction de connexion WebSocket
  const connect = useCallback(
    (url: string) => {
      // Évite les connexions multiples
      if (
        wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING
      ) {
        return;
      }

      // Ouvre une connexion WebSocket vers le serveur.
      // Le navigateur envoie automatiquement une requête HTTP avec "Upgrade: websocket",
      // ce qui déclenche wsHandler() côté serveur et enregistre cette socket dans l'annuaire.
      const ws = new WebSocket(url);
      wsRef.current = ws;

      // Appelé quand la connexion WebSocket est établie avec succès.
      ws.onopen = () => {
        // Réinitialise le délai de reconnexion après une connexion réussie
        reconnectDelayRef.current = RECONNECT_CONFIG.initialDelay;
        dernierMessageRecuRef.current = Date.now();
      };

      // Appelé quand le serveur envoie un message via WebSocket
      ws.onmessage = handleMessage;

      ws.onerror = () => {
        client.logger.warn('WS: Connection error');
      };

      // Appelé quand la connexion WebSocket est fermée (réseau coupé, serveur restart, fermeture volontaire...).
      // Gère la reconnexion automatique avec backoff exponentiel : 1s → 2s → 4s → 8s → ... → 30s max.
      ws.onclose = (event) => {
        client.logger.info(
          'WS: Disconnected (code=%d, reason=%s)',
          event.code,
          event.reason,
        );
        wsRef.current = null;

        // Ne pas reconnecter si la fermeture est intentionnelle (code 1000)
        // ou si l'utilisateur n'est pas autorisé (code 1008)
        if (event.code === 1000 || event.code === 1008) {
          return;
        }

        // Reconnexion avec backoff exponentiel
        const delay = reconnectDelayRef.current;
        client.logger.info('WS: Reconnecting in %dms', delay);

        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectDelayRef.current = Math.min(
            reconnectDelayRef.current * RECONNECT_CONFIG.multiplier,
            RECONNECT_CONFIG.maxDelay,
          );
          connect(url);
        }, delay);
      };
    },
    [client, handleMessage],
  );

  // Ferme proprement la connexion WebSocket (code 1000 = pas de reconnexion auto via onclose)
  const closeConnection = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'Fermeture manuelle');
      wsRef.current = null;
    }
  }, []);

  // Reconnecte en utilisant l'URL WebSocket courante
  const reconnect = useCallback(() => {
    const websocketUrl = websocketState?.websocket_url;
    if (websocketUrl) {
      connect(websocketUrl);
    }
  }, [connect, websocketState?.websocket_url]);

  // Effect principal : connecte/déconnecte quand l'URL ou le token change
  useEffect(() => {
    const websocketUrl = websocketState?.websocket_url;

    if (!websocketUrl) {
      return;
    }

    connect(websocketUrl);

    // Vérification périodique de la fraîcheur de la connexion.
    // Le serveur envoie un heartbeat toutes les 30s. Si on n'a rien reçu depuis 75s,
    // la connexion est probablement morte (ex: Cloud Run a tué l'instance).
    verificationFraicheurIntervalRef.current = setInterval(
      verifierConnexionMorte,
      30_000,
    );

    // Quand l'onglet redevient visible, vérifier immédiatement la connexion.
    // Couvre le cas où l'onglet était en arrière-plan pendant un déploiement Cloud Run.
    // Garde pour React Native où document n'existe pas.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        verifierConnexionMorte();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    // Cleanup : ferme proprement la connexion et annule les timers
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange,
        );
      }
      if (verificationFraicheurIntervalRef.current) {
        clearInterval(verificationFraicheurIntervalRef.current);
        verificationFraicheurIntervalRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        // Code 1000 = fermeture normale, évite la reconnexion automatique
        wsRef.current.close(1000, 'Component unmount');
        wsRef.current = null;
      }
    };
  }, [
    websocketState?.websocket_url,
    sessionToken,
    connect,
    verifierConnexionMorte,
  ]);

  return {close: closeConnection, connect: reconnect};
}
